import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { FoodRuntime } from "./food-runtime";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import {
  createBotBehaviorState,
  relocateBotBehaviorState,
  stepBotBehavior,
  type BotBehavior,
  type BotBehaviorState,
  type BotRecoveryState,
  type BotTarget,
  type PerceivedSnake,
} from "../simulation/bot-behavior";
import {
  BotRoster,
  getMaximumBotLevel,
  type BotEntity,
  type BotPosition,
  strengthCueForLevel,
} from "../simulation/bot-roster";
import { getPlayerGrowthProgress } from "../simulation/player-growth";
import { SeededRandom } from "../simulation/seeded-random";
import {
  addSpawnClearance,
  createSnakeSpawnExclusions,
  type SpawnExclusionZone,
} from "../simulation/spawn-exclusions";
import {
  advanceSnakeBodyTrailPath,
  createSnakeBodyTrail,
  materializeSnakeBodyTrail,
  triggerPlayerBodyRipple,
  type SnakeBodyTrailState,
} from "../simulation/player-body-trail";

export interface BotRuntimeSnapshot {
  readonly bots: readonly ActiveBotEntity[];
}

export interface BotRenderSnapshot {
  readonly current: readonly ActiveBotEntity[];
  readonly previous: readonly ActiveBotEntity[];
}

export interface ActiveBotEntity extends BotEntity {
  readonly behavior: BotBehavior;
  readonly body: SnakeBodyTrailState;
  readonly directionRadians: number;
  readonly foodCollected: number;
  readonly growth: number;
  readonly lastPickup: BotPickupState | null;
  readonly recovery: BotRecoveryState;
  readonly target: BotTarget;
}

export interface BotPickupState {
  readonly foodIds: readonly string[];
  readonly growth: number;
  readonly levelUp: boolean;
  readonly occurredAtMs: number;
  readonly revision: number;
}

interface BotGrowthState {
  readonly foodCollected: number;
  readonly growth: number;
  readonly lastPickup: BotPickupState | null;
  readonly revision: number;
}

type BotStepSource = Pick<PlayerRuntime, "subscribeFixedStep"> &
  Partial<Pick<PlayerRuntime, "getSnapshot" | "subscribe">>;

export class BotRuntime {
  private behaviorRandom = new SeededRandom("inactive-bot-decisions");
  private readonly behaviorStates = new Map<string, BotBehaviorState>();
  private readonly bodyStates = new Map<string, SnakeBodyTrailState>();
  private readonly growthStates = new Map<string, BotGrowthState>();
  private levelMaximum = getMaximumBotLevel(
    gameplayConfig.growth.startingLevel,
    gameplayConfig.bots,
  );
  private playerLevel: number = gameplayConfig.growth.startingLevel;
  private roster: BotRoster | null = null;
  private previousBots: readonly ActiveBotEntity[] = [];
  private simulationTimeMs = 0;

  public constructor(
    flow: GameFlow,
    private seed: string,
    private readonly foodRuntime?: Pick<
      FoodRuntime,
      "collectOverlapping" | "getSnapshot"
    >,
    private readonly stepSource?: BotStepSource,
  ) {
    stepSource?.subscribeFixedStep((stepMilliseconds, simulationTimeMs) => {
      this.step(stepMilliseconds, simulationTimeMs);
    });
    stepSource?.subscribe?.((snapshot) => {
      this.synchronizeLevelLimit(snapshot.player?.level);
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public getSnapshot(): BotRuntimeSnapshot {
    this.synchronizeLevelLimit();
    const bots = this.roster?.getSnapshot() ?? [];
    return {
      bots: bots.map((rosterBot) => {
        const bot = this.materializeBot(rosterBot);
        const state = this.requireBehaviorState(bot.id);
        const growthState = this.requireGrowthState(bot.id);
        return {
          ...bot,
          behavior: state.behavior,
          body: materializeSnakeBodyTrail(
            this.requireBodyState(bot.id),
            this.createBodySource(bot, state),
            gameplayConfig.playerBody,
          ),
          directionRadians: state.directionRadians,
          foodCollected: growthState.foodCollected,
          growth: growthState.growth,
          lastPickup: this.cloneLastPickup(growthState.lastPickup),
          position: { ...state.position },
          recovery: { ...state.recovery },
          target: {
            ...state.target,
            position: { ...state.target.position },
          },
        };
      }),
    };
  }

  public getRenderSnapshot(): BotRenderSnapshot {
    return {
      current: this.getSnapshot().bots,
      previous: this.previousBots,
    };
  }

  public applyCombatPosition(identifier: string, position: BotPosition): void {
    const state = this.requireBehaviorState(identifier);
    const relocated = relocateBotBehaviorState(
      state,
      position,
      this.simulationTimeMs,
    );
    this.behaviorStates.set(identifier, relocated);
    const existingBody = this.bodyStates.get(identifier);
    this.bodyStates.set(
      identifier,
      existingBody === undefined
        ? this.createResetBodyState(
            this.requireRosterBot(identifier),
            relocated.position,
          )
        : advanceSnakeBodyTrailPath(
            existingBody,
            this.createBodySource(this.requireRosterBot(identifier), relocated),
            gameplayConfig.playerBody,
          ),
    );
    this.resetBotRenderInterpolation(identifier);
  }

  public applyBodyCollision(
    identifier: string,
    position: BotPosition,
    directionRadians: number,
  ): void {
    const state = this.requireBehaviorState(identifier);
    const relocated = relocateBotBehaviorState(
      state,
      position,
      this.simulationTimeMs,
    );
    this.behaviorStates.set(identifier, { ...relocated, directionRadians });
    this.bodyStates.set(
      identifier,
      advanceSnakeBodyTrailPath(
        this.requireBodyState(identifier),
        this.createBodySource(this.requireRosterBot(identifier), relocated),
        gameplayConfig.playerBody,
      ),
    );
    this.resetBotRenderInterpolation(identifier);
  }

  public setSeed(seed: string): void {
    this.seed = seed;
  }

  public setGrowth(identifier: string, growth: number, ripple: boolean): void {
    const current = this.requireGrowthState(identifier);
    getPlayerGrowthProgress(growth, gameplayConfig.growth);
    this.growthStates.set(identifier, { ...current, growth });
    if (ripple) {
      this.bodyStates.set(
        identifier,
        triggerPlayerBodyRipple(
          this.requireBodyState(identifier),
          this.simulationTimeMs,
        ),
      );
    }
    this.resetBotRenderInterpolation(identifier);
  }

  public remove(identifier: string): ActiveBotEntity | null {
    const roster = this.requireRoster();
    const exists = roster
      .getSnapshot()
      .some((candidate) => candidate.id === identifier);
    if (!exists) {
      return null;
    }
    const state = this.behaviorStates.get(identifier);
    if (state === undefined) {
      throw new Error(`Bot behavior state is missing for ${identifier}`);
    }
    const body = this.requireBodyState(identifier);
    const growthState = this.requireGrowthState(identifier);
    const removed = roster.remove(identifier);
    this.behaviorStates.delete(identifier);
    this.bodyStates.delete(identifier);
    this.growthStates.delete(identifier);
    this.previousBots = this.previousBots.filter(
      (bot) => bot.id !== identifier,
    );
    const grown = this.materializeBot(removed, growthState);
    return {
      ...grown,
      behavior: state.behavior,
      body: materializeSnakeBodyTrail(
        body,
        this.createBodySource(grown, state),
        gameplayConfig.playerBody,
      ),
      directionRadians: state.directionRadians,
      foodCollected: growthState.foodCollected,
      growth: growthState.growth,
      lastPickup: this.cloneLastPickup(growthState.lastPickup),
      position: { ...state.position },
      recovery: { ...state.recovery },
      target: {
        ...state.target,
        position: { ...state.target.position },
      },
    };
  }

  public spawn(level: number, near: BotPosition | null): string {
    this.synchronizeLevelLimit();
    const roster = this.requireRoster();
    const rosterBot = roster.spawn(level, near);
    this.growthStates.set(rosterBot.id, this.createGrowthState(level));
    const bot = this.materializeBot(rosterBot);
    const state = this.createBehaviorState(
      bot,
      this.createPerceivedSnakes(roster.getSnapshot()),
    );
    this.behaviorStates.set(bot.id, state);
    this.bodyStates.set(bot.id, this.createResetBodyState(bot, bot.position));
    return bot.id;
  }

  public replenish(
    player: {
      readonly body: readonly SpawnExclusionZone[];
      readonly position: BotPosition;
      readonly radius: number;
    } | null,
  ): string | null {
    this.synchronizeLevelLimit();
    const roster = this.requireRoster();
    const currentBots = this.getSnapshot().bots;
    const playerExclusions =
      player === null
        ? []
        : addSpawnClearance(
            createSnakeSpawnExclusions([player]),
            gameplayConfig.bots.regenerationPlayerClearance,
          );
    const botExclusions = addSpawnClearance(
      createSnakeSpawnExclusions(
        currentBots.map((bot) => ({
          body: bot.body.segments,
          position: bot.position,
          radius: bot.collision.radius,
        })),
      ),
      gameplayConfig.bots.minimumSpacing,
    );
    const rosterBot = roster.replenish([...playerExclusions, ...botExclusions]);
    if (rosterBot === null) {
      return null;
    }
    this.growthStates.set(
      rosterBot.id,
      this.createGrowthState(rosterBot.traits.level),
    );
    const bot = this.materializeBot(rosterBot);
    const state = this.createBehaviorState(
      bot,
      this.createPerceivedSnakes(roster.getSnapshot()),
    );
    this.behaviorStates.set(bot.id, state);
    this.bodyStates.set(bot.id, this.createResetBodyState(bot, bot.position));
    return bot.id;
  }

  public resetRenderInterpolation(): void {
    this.previousBots = this.getSnapshot().bots;
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "results") {
      return;
    }
    if (snapshot.scene === "home") {
      this.roster = null;
      this.behaviorStates.clear();
      this.bodyStates.clear();
      this.growthStates.clear();
      this.previousBots = [];
      this.simulationTimeMs = 0;
      return;
    }
    this.behaviorRandom = new SeededRandom(`${this.seed}:bot-decisions`);
    this.behaviorStates.clear();
    this.bodyStates.clear();
    this.growthStates.clear();
    this.simulationTimeMs = 0;
    this.synchronizeLevelLimit();
    this.roster = new BotRoster({
      arena: gameplayConfig.arena,
      config: gameplayConfig.bots,
      cosmeticRandom: new SeededRandom(`${this.seed}:bot-cosmetics`),
      gameplayRandom: new SeededRandom(`${this.seed}:bot-traits`),
      initialExclusions: [
        {
          position: gameplayConfig.movement.initialPosition,
          radius: gameplayConfig.bots.initialPlayerExclusionRadius,
        },
      ],
      levelSizes: gameplayConfig.growth.sizes,
      maximumLevel: this.levelMaximum,
      playerLevel:
        this.stepSource?.getSnapshot?.().player?.level ??
        gameplayConfig.growth.startingLevel,
      spawnRandom: new SeededRandom(`${this.seed}:bot-spawns`),
    });
    const bots = this.roster.getSnapshot();
    for (const rosterBot of bots) {
      this.growthStates.set(
        rosterBot.id,
        this.createGrowthState(rosterBot.traits.level),
      );
    }
    const snakes = this.createPerceivedSnakes(bots);
    for (const rosterBot of bots) {
      const bot = this.materializeBot(rosterBot);
      const state = this.createBehaviorState(bot, snakes);
      this.behaviorStates.set(bot.id, state);
      this.bodyStates.set(
        bot.id,
        createSnakeBodyTrail(
          this.createInitialBodySource(bot),
          gameplayConfig.playerBody,
        ),
      );
    }
    this.resetRenderInterpolation();
  }

  private createBehaviorState(
    bot: BotEntity,
    snakes: readonly PerceivedSnake[],
  ): BotBehaviorState {
    return createBotBehaviorState({
      arena: gameplayConfig.arena,
      bot,
      config: gameplayConfig.bots,
      foods: this.foodRuntime?.getSnapshot().foods ?? [],
      random: this.behaviorRandom,
      simulationTimeMs: this.simulationTimeMs,
      snakes,
    });
  }

  private createPerceivedSnakes(
    bots: readonly BotEntity[],
  ): readonly PerceivedSnake[] {
    const player = this.stepSource?.getSnapshot?.().player ?? null;
    const playerSnake: readonly PerceivedSnake[] =
      player?.alive !== true
        ? []
        : [{ id: "player", level: player.level, position: player.position }];
    return [
      ...playerSnake,
      ...bots.map((rosterBot) => {
        const bot = this.materializeBot(rosterBot);
        return {
          id: bot.id,
          level: bot.traits.level,
          position: this.behaviorStates.get(bot.id)?.position ?? bot.position,
        };
      }),
    ];
  }

  private requireBehaviorState(identifier: string): BotBehaviorState {
    const state = this.behaviorStates.get(identifier);
    if (state === undefined) {
      throw new Error(`Bot behavior state is missing for ${identifier}`);
    }
    return state;
  }

  private step(stepMilliseconds: number, simulationTimeMs: number): void {
    this.synchronizeLevelLimit();
    if (this.roster === null) {
      return;
    }
    this.previousBots = this.getSnapshot().bots;
    this.simulationTimeMs = simulationTimeMs;
    const foods = this.foodRuntime?.getSnapshot().foods ?? [];
    const bots = this.roster
      .getSnapshot()
      .map((bot) => this.materializeBot(bot));
    const snakes = this.createPerceivedSnakes(bots);
    const nextStates: [BotEntity, BotBehaviorState][] = [];
    for (const bot of bots) {
      const state = this.requireBehaviorState(bot.id);
      nextStates.push([
        bot,
        stepBotBehavior({
          arena: gameplayConfig.arena,
          bot: { ...bot, position: state.position },
          config: gameplayConfig.bots,
          foods,
          random: this.behaviorRandom,
          simulationTimeMs,
          snakes,
          state,
          stepMilliseconds,
        }),
      ]);
    }
    for (const [bot, state] of nextStates) {
      this.behaviorStates.set(bot.id, state);
      this.bodyStates.set(
        bot.id,
        advanceSnakeBodyTrailPath(
          this.requireBodyState(bot.id),
          this.createBodySource(bot, state),
          gameplayConfig.playerBody,
        ),
      );
      this.collectFood(bot, state);
    }
  }

  private resetBotRenderInterpolation(identifier: string): void {
    const current = this.getSnapshot().bots.find(
      (bot) => bot.id === identifier,
    );
    if (current === undefined) {
      this.previousBots = this.previousBots.filter(
        (bot) => bot.id !== identifier,
      );
      return;
    }
    const previousIndex = this.previousBots.findIndex(
      (bot) => bot.id === identifier,
    );
    this.previousBots =
      previousIndex === -1
        ? [...this.previousBots, current]
        : this.previousBots.map((bot, index) =>
            index === previousIndex ? current : bot,
          );
  }

  private createBodySource(bot: BotEntity, state: BotBehaviorState) {
    return {
      directionRadians: state.directionRadians,
      level: bot.traits.level,
      position: state.position,
      size: bot.collision.radius,
    };
  }

  private cloneLastPickup(
    pickup: BotPickupState | null,
  ): BotPickupState | null {
    return pickup === null
      ? null
      : {
          ...pickup,
          foodIds: [...pickup.foodIds],
        };
  }

  private collectFood(bot: BotEntity, state: BotBehaviorState): void {
    if (this.foodRuntime === undefined) {
      return;
    }
    const collected = this.foodRuntime.collectOverlapping(
      state.position,
      bot.collision.radius,
    );
    if (collected.length === 0) {
      return;
    }
    const current = this.requireGrowthState(bot.id);
    const growth = collected.reduce(
      (total, food) => total + food.growth,
      current.growth,
    );
    const previousLevel = Math.min(
      getPlayerGrowthProgress(current.growth, gameplayConfig.growth).level,
      this.levelMaximum,
    );
    const level = Math.min(
      getPlayerGrowthProgress(growth, gameplayConfig.growth).level,
      this.levelMaximum,
    );
    const revision = current.revision + 1;
    this.growthStates.set(bot.id, {
      foodCollected: current.foodCollected + collected.length,
      growth,
      lastPickup: {
        foodIds: collected.map((food) => food.id),
        growth: growth - current.growth,
        levelUp: level > previousLevel,
        occurredAtMs: this.simulationTimeMs,
        revision,
      },
      revision,
    });
    this.bodyStates.set(
      bot.id,
      triggerPlayerBodyRipple(
        this.requireBodyState(bot.id),
        this.simulationTimeMs,
      ),
    );
  }

  private createGrowthState(level: number): BotGrowthState {
    const growth = gameplayConfig.growth.thresholds[level - 1];
    if (growth === undefined) {
      throw new Error(
        `Growth threshold is missing for bot level ${String(level)}`,
      );
    }
    return {
      foodCollected: 0,
      growth,
      lastPickup: null,
      revision: 0,
    };
  }

  private materializeBot(
    bot: BotEntity,
    state: BotGrowthState = this.requireGrowthState(bot.id),
  ): BotEntity {
    const progress = getPlayerGrowthProgress(
      state.growth,
      gameplayConfig.growth,
    );
    const level = Math.min(
      progress.level,
      bot.traits.challenger ? this.levelMaximum : this.playerLevel,
    );
    const size = gameplayConfig.growth.sizes[level - 1];
    if (size === undefined) {
      throw new Error(`Growth size is missing for bot level ${String(level)}`);
    }
    return {
      ...bot,
      collision: { radius: size },
      cosmetic: {
        ...bot.cosmetic,
        strengthCue: strengthCueForLevel(level, bot.traits.challenger),
      },
      traits: { ...bot.traits, level },
    };
  }

  private synchronizeLevelLimit(playerLevel?: number): void {
    const currentPlayerLevel =
      playerLevel ??
      this.stepSource?.getSnapshot?.().player?.level ??
      gameplayConfig.growth.startingLevel;
    const levelMaximum = getMaximumBotLevel(
      currentPlayerLevel,
      gameplayConfig.bots,
    );
    this.playerLevel = currentPlayerLevel;
    this.levelMaximum = levelMaximum;
    this.roster?.setLevelRange(currentPlayerLevel, this.levelMaximum);
    const rosterBots = this.roster?.getSnapshot() ?? [];
    for (const [identifier, state] of this.growthStates) {
      const rosterBot = rosterBots.find((bot) => bot.id === identifier);
      const maximumLevel =
        rosterBot?.traits.challenger === true
          ? levelMaximum
          : currentPlayerLevel;
      const maximumGrowth = gameplayConfig.growth.thresholds[maximumLevel - 1];
      if (maximumGrowth === undefined) {
        throw new Error(
          `Growth threshold is missing for bot level ${String(maximumLevel)}`,
        );
      }
      if (
        getPlayerGrowthProgress(state.growth, gameplayConfig.growth).level >
        maximumLevel
      ) {
        this.growthStates.set(identifier, {
          ...state,
          growth: maximumGrowth,
        });
      }
    }
  }

  private createInitialBodySource(
    bot: BotEntity,
    position: BotPosition = bot.position,
  ) {
    return {
      directionRadians: Math.atan2(
        position.y - gameplayConfig.arena.height / 2,
        position.x - gameplayConfig.arena.width / 2,
      ),
      level: bot.traits.level,
      position,
      size: bot.collision.radius,
    };
  }

  private createResetBodyState(
    bot: BotEntity,
    position: BotPosition,
  ): SnakeBodyTrailState {
    const seeded = createSnakeBodyTrail(
      this.createInitialBodySource(bot, position),
      gameplayConfig.playerBody,
    );
    return {
      path: [{ ...position }],
      rippleStartedAtMs: null,
      segments: seeded.segments.map((segment) => ({
        position: { ...position },
        radius: segment.radius,
      })),
    };
  }

  private requireBodyState(identifier: string): SnakeBodyTrailState {
    const state = this.bodyStates.get(identifier);
    if (state === undefined) {
      throw new Error(`Bot body state is missing for ${identifier}`);
    }
    return state;
  }

  private requireGrowthState(identifier: string): BotGrowthState {
    const state = this.growthStates.get(identifier);
    if (state === undefined) {
      throw new Error(`Bot growth state is missing for ${identifier}`);
    }
    return state;
  }

  private requireRosterBot(identifier: string): BotEntity {
    const bot = this.requireRoster()
      .getSnapshot()
      .find((candidate) => candidate.id === identifier);
    if (bot === undefined) {
      throw new Error(`Bot roster state is missing for ${identifier}`);
    }
    return this.materializeBot(bot);
  }

  private requireRoster(): BotRoster {
    if (this.roster === null) {
      throw new Error("Bot roster is unavailable outside gameplay");
    }
    return this.roster;
  }
}
